import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarCheck,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  UserRound,
  Video,
} from "lucide-react";
import PageSeo from "../components/PageSeo";
import Nd1SiteNav from "./components/Nd1SiteNav";
import Nd1SiteFooter from "./components/Nd1SiteFooter";
import FlowStep from "./components/FlowStep";
import Nd1DemoVideoPlaceholder from "./components/Nd1DemoVideoPlaceholder";
import Nd1FlowBreakdown from "./components/Nd1FlowBreakdown";
import { APPLYING_HOW_IT_WORKS_VIDEO_ID } from "../lib/youtubeVideos";
import { buildBreadcrumbJsonLd, buildPublicPageUrl } from "../lib/pageSeo";
import { ND1_HIW_APPLYING } from "./nd1Paths";
import "./nd1.css";

export default function Nd1HowItWorksApplyingPage() {
  const pageUrl = buildPublicPageUrl(ND1_HIW_APPLYING);

  return (
    <div className="nd1-root nd1-root--seekers">
      <PageSeo
        title="How it works — Applying"
        description="See exactly how applying on JOBUWIN works: find genuine jobs, build your profile, submit a video pitch, and track your applications."
        path={ND1_HIW_APPLYING}
        canonicalPath={ND1_HIW_APPLYING}
        schemaType="website"
        jsonLd={buildBreadcrumbJsonLd([
          { name: "Home", url: buildPublicPageUrl("/") },
          { name: "How it works — Applying", url: pageUrl },
        ])}
      />

      <a href="#nd1-main" className="nd1-skip-link">
        Skip to content
      </a>

      <Nd1SiteNav />

      <main id="nd1-main">
        <section className="nd1-shell nd1-section">
          <div className="nd1-eyebrow">How it works · For applying</div>
          <h1 className="nd1-js-hero__title">
            Get hired as a <span className="nd1-ink-green">real person</span> — not a CV in a pile.
          </h1>

          <div className="nd1-section nd1-section--tight">
            <Nd1DemoVideoPlaceholder
              slotId="applying-journey"
              playLabel="Play the applicant demo"
              videoId={APPLYING_HOW_IT_WORKS_VIDEO_ID}
              thumbnailAlt="How applying on JOBUWIN works"
            />

            <div className="nd1-section nd1-section--tight">
              <Nd1FlowBreakdown>
                <FlowStep
                  icon={ShieldCheck}
                  title="Verify you're genuine"
                  description="Register with your email and mobile and confirm they are real. We want genuine people to apply for jobs who are real and honest."
                />
                <FlowStep
                  icon={UserRound}
                  title="Build a genuine profile"
                  description="Go beyond a CV — show your experience, your strengths, and what your real capabilities are for each role you're genuinely suited to."
                />
                <FlowStep
                  icon={Video}
                  title="Watch hiring manager pitch videos"
                  description="Hiring managers and recruiters explain on video exactly what they're looking for — so you understand your suitability before you apply."
                />
                <FlowStep
                  icon={Sparkles}
                  title="Submit your own pitch"
                  description="Don't overthink it — just be honest, transparent and real. Talk through your skills and expertise, where you'd excel, and where you wouldn't. Honesty wins here."
                />
                <FlowStep
                  icon={Send}
                  title="Apply directly & track"
                  description="Apply to verified jobs in one place and follow your status the whole way through."
                />
                <FlowStep
                  icon={CalendarCheck}
                  title="Get invited & interview"
                  description="When an employer likes your pitch, you're invited to talk — straight to a real conversation."
                />
              </Nd1FlowBreakdown>
            </div>
          </div>

          <div className="nd1-section nd1-section--tight">
            <div className="nd1-js-cta-band">
              <span className="nd1-js-cta-band__icon">
                <UserRound className="nd1-icon" aria-hidden="true" />
              </span>
              <p className="nd1-js-cta-band__copy nd1-js-cta-band__copy--sm">
                Ready to find <span className="nd1-ink-green">genuine jobs?</span> Create your profile, record
                your intro, and start applying to verified roles today.
              </p>
              <Link to="/signup?type=applicant" className="nd1-btn nd1-btn--primary">
                <Search className="nd1-icon" aria-hidden="true" />
                Find genuine jobs
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Nd1SiteFooter />
    </div>
  );
}
